import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import Layout from '../../components/Layout.js';
import HeroBlobs from '../../components/HeroBlobs.js';
import LazyImg from '../../components/LazyImg.js';
import InfoCard from './InfoCard.js';

import linkedin from '../../assets/logos/linkedin.webp';
import github from '../../assets/logos/github.webp';
import orcid from '../../assets/logos/ORCID.png';
import resumeFile from '../../assets/files/resume.pdf';
import neuralNetIcon from '../../assets/icons/neural-net.svg';
import gameControllerIcon from '../../assets/icons/game-controller.svg';
import browserIcon from '../../assets/icons/browser.svg';
import complexityHierarchyIcon from '../../assets/icons/complexity-hierarchy.svg';

import './home.css';





const LATEST_PROJECTS = [
    {
        tag: 'Web · React',
        title: 'Rifting Wrapped',
        desc: 'React app with 200+ users for tracking your League of Legends statistics and trends. Riot approved.',
        techs: ['React', 'MongoDB', 'Render'],
        href: '/projects/rifting-wrapped',
        github: 'https://github.com/notred27/RiftingWrapped',
        live: 'https://www.riftingwrapped.com',
    },

    {
        tag: 'Chrome · ML',
        title: 'URaBOT',
        desc: 'Fine-tuned DeBERTa V3 detecting bot-generated content on Twitter & Bluesky. 85% accuracy · 0.79 F1.',
        techs: ['JavaScript', 'Transformers'],
        href: '/projects/urabot',
        github: 'https://github.com/notred27/URaBOT',
        live: 'https://huggingface.co/spaces/mreidy3/URaBot',
    },
    {
        tag: 'ML · Research',
        title: 'AI-Generated Image Classifiers',
        desc: 'Deep learning + CFA interpolation classifiers for identifying AIGC. Published at IEEE BigData.',
        techs: ['Python', 'MATLAB', 'IEEE'],
        href: '/projects/aigc',
        github: 'https://github.com/notred27/AI-Generated-Image-Classifiers',
        live: 'https://ieeexplore.ieee.org/document/10386096',
    },
    // {
    //     tag: 'Discord · Python',
    //     title: 'MTG Ranking Bot',
    //     desc: 'Tracks Commander match history, placements, and player rankings with a persistent SQLite backend.',
    //     techs: ['Python', 'SQLite', 'Docker'],
    //     href: '/projects/mtg-bot',
    //     github: 'https://github.com/notred27/MTG-Ranking-Bot',
    //     live: null,
    // },

];


function ProjectCard({ tag, title, desc, techs, href, github, live }) {
    return (
        <div className="project-card">
            <div className="project-card-main">
                <span className="project-tag">{tag}</span>
                <Link to={href} className="project-title-link">
                    <h3 className="project-title">{title}</h3>
                </Link>
                <p className="project-desc">{desc}</p>
                <div className="project-techs">
                    {techs.map(t => <span className="tech-pill" key={t}>{t}</span>)}
                </div>
            </div>

            <div className="project-card-actions">
                {live && (
                    <a href={live} target="_blank" rel="noopener noreferrer" className="project-link-btn project-link-live">
                        <ion-icon name="open-outline" />
                        Live demo
                    </a>
                )}
                {github && (
                    <a href={github} target="_blank" rel="noopener noreferrer" className="project-link-btn project-link-github">
                        <ion-icon name="logo-github" />
                        GitHub
                    </a>
                )}
                <Link to={href} className="project-link-btn project-link-details">
                    Details
                    <ion-icon name="arrow-forward-outline" />
                </Link>
            </div>
        </div>
    );
}


function Home() {
    const heroRef = useRef();

    useEffect(() => {
        const handleScroll = () => {
            if (heroRef.current) {
                heroRef.current.style.transform = `translateY(${window.scrollY * 0.3}px)`;
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <Layout pageIndex={0}>

            {/* ── HERO ─────────────────────────────────────────── */}
            <div className="hero-outer noise">
                <HeroBlobs />
                <div className="hero-inner" ref={heroRef}>


                    <img
                        src='/images/hero.webp'
                        alt="Michael Reidy"
                        className="hero-photo"
                        fetchpriority="high"
                        decoding="async"
                    />

                    <div className="hero-text">
                        <p className="hero-leadup">Hi, I'm</p>
                        <h1 className="hero-name">Michael Reidy</h1>
                        <p className="hero-eyebrow">Software Engineer</p>
                        <br />

                        <div className="hero-stats">
                            <div className="hero-stat">
                                <strong className="hero-stat-value">Published</strong>
                                <span className="hero-stat-label">IEEE BigData</span>
                            </div>
                            <div className="hero-stat">
                                <strong className="hero-stat-value">4+</strong>
                                <span className="hero-stat-label">Hackathon Wins</span>
                            </div>
                            <div className="hero-stat">
                                <strong className="hero-stat-value">200+</strong>
                                <span className="hero-stat-label">Active App Users</span>
                            </div>
                        </div>

                        <br />
                        <div className="hero-links">
                            <a href="https://github.com/notred27" className="hero-icon-link" aria-label="GitHub">
                                <img src={github} alt="" className="hero-icon-img" />
                            </a>
                            <a href="https://www.linkedin.com/in/michael-p-reidy/" className="hero-icon-link" aria-label="LinkedIn">
                                <img src={linkedin} alt="" className="hero-icon-img" />
                            </a>
                            <a href="https://orcid.org/0009-0002-6850-032X" className="hero-icon-link" aria-label="ORCID">
                                <img src={orcid} alt="" className="hero-icon-img" />
                            </a>
                            <a href={resumeFile} className="hero-icon-link" aria-label="Resume">
                                <ion-icon name="document-text-outline" style={{ fontSize: '24px', color: 'white' }} />
                            </a>

                        </div>
                    </div>


                </div>
            </div>
            <section className="home-section intro-section">
                <div className="intro-grid">
                    <div className="intro-text">
                        <p className="section-eyebrow">A bit about me</p>
                        <h2 className="section-title">Turning research into reality</h2>
                        <p className="hero-bio">
                            I'm a software engineer with a focus on <strong className="bio-highlight">full-stack development</strong> and <strong className="bio-highlight">applied ML</strong>.
                        </p>

                        <p className="hero-bio">
                            During my B.S. in Computer Science at the <strong className="bio-highlight">University of Rochester</strong> (Phi
                            Beta Kappa, 3.87 GPA), I conducted AI-generated content detection research published at{' '}
                            <strong className="bio-highlight">IEEE BigData</strong>. I also contributed to a <strong className="bio-highlight">NSF REU</strong> by proving complexity
                            results in computational social choice. Through my research, I make AIGC-detection software with <a className="bio-highlight" href='/projects/urabot'>URaBOT</a> and <a className="bio-highlight" href='/projects/aigc'>ML Classifiers</a>.
                        </p>

                        <p className="hero-bio">
                            On the side, I like to build tools, apps, and games with my friends. One of these projects, <a className="bio-highlight" href='https://www.riftingwrapped.com/'>Rifting Wrapped</a>, is a League of Legends stats app I
                            built that has <strong className="bio-highlight">200+ active users</strong>.
                        </p>

                        <p className="hero-bio">
                            I'm always looking for the next interesting project, whether that's a paper, a product, or a weekend hackathon.
                            Feel free to connect and reach out!
                        </p>

                        <Link to="/about" className="hero-cta">
                            Learn More <span className="hero-cta-arrow">→</span>
                        </Link>
                    </div>

                    <div className="intro-image">
                        <LazyImg
                            imageKey="galla"
                            alt="Michael Reidy"
                            className="intro-photo"
                        />
                    </div>
                </div>
            </section>




            {/* ── PASSIONS ─────────────────────────────────────── */}
            <section className="home-section">
                <p className="section-eyebrow">What drives me</p>
                <h2 className="section-title">Areas I'm passionate about</h2>

                <div className="passion-grid">
                    <InfoCard
                        title="Machine Learning"
                        desc="AIGC detection, image classifiers, and IEEE publications."
                        linkText="Explore projects"
                        linkDir="/projects"
                        image={neuralNetIcon}
                    />
                    <InfoCard
                        title="Game Design"
                        desc="4x first-place hackathon projects and more."
                        linkText="Explore games"
                        linkDir="/projects#games"
                        image={gameControllerIcon}
                    />
                    <InfoCard
                        title="Web Development"
                        desc="Full-stack apps, PWAs, and real-time tools."
                        linkText="Explore websites"
                        linkDir="/projects#websites"
                        image={browserIcon}
                    />
                    <InfoCard
                        title="Computational Complexity"
                        desc="Electoral control and P vs NP critiques."
                        linkText="Explore papers"
                        linkDir="/papers"
                        image={complexityHierarchyIcon}
                    />
                </div>
            </section>


            {/* ── LATEST PROJECTS ──────────────────────────────── */}
            <section className="home-section">
                <p className="section-eyebrow">Selected work</p>
                <h2 className="section-title">Featured projects</h2>

                <div className="project-grid">
                    {LATEST_PROJECTS.map(p => <ProjectCard key={p.title} {...p} />)}
                </div>

                {/* <div className="section-cta-row">
                    <Link to="/projects" className="outline-btn">
                        View all projects →
                    </Link>
                </div> */}
            </section>




            {/* ── CONTACT BAND ─────────────────────────────────── */}
            <section className="contact-band noise">
                <div className="contact-band-text">
                    <div>

                        <h2 className="contact-band-title">Let's build something together.</h2>
                        <p className="contact-band-sub">
                            Open to new job opportunities, collaborations, and interesting projects!
                        </p>
                    </div>

                    <Link to="/contact" className="hero-cta">
                        Get in touch <span className="hero-cta-arrow">→</span>
                    </Link>
                </div>

            </section>


        </Layout>
    );
}

export default Home;